import { createContext, use, type ComponentProps } from "react";
import { Select as SelectPrimitive } from "@base-ui/react/select";
import { cn } from "cn";
import { controlStyles } from "./control-styles";
import { labelStyles } from "./label";
import { merge } from "./merge";

/**
 * `md` is the single-line control every other field is: `--control-height`, field type, the
 * sunken well. `sm` is the compact select — the one that sits inside a row of content rather
 * than in a form column, so it reads as a chip with the secondary button's lip instead of a
 * field with a well.
 *
 * `C1 Components · Select` also specifies an `lg` at 48. It is marked PROPOSED there and no
 * screen draws it, so it is deliberately absent: a size with no caller is a size nobody has
 * checked, and the board says as much in its own `WHERE` row — "No screen yet."
 */
export type SelectSize = "sm" | "md";

/**
 * Geometry and skin per size. Nothing here is one screen's: a width, a lane and a margin are
 * what a board decides, and they stay with the caller as ordinary utilities. What is here is
 * what "a compact select" is anywhere it appears.
 *
 * The two skins are not a tidying opportunity. `md` sits in a form column and reads as a field —
 * `--control-border` and a sunken well, both of which arrive with `controlStyles`, which is why
 * its trigger entry is empty. `sm` sits inside a row of content, where a well would look like a
 * hole in the row, so it reads as a chip: `--input` with the secondary button's lip.
 */
const SIZES: Record<
  SelectSize,
  {
    trigger: string;
    menu: string;
    item: string;
    indicator: string;
    /** The check the indicator lane draws, at the weight the board gives each size's stroke. */
    check: { size: number; strokeWidth: number };
  }
> = {
  sm: {
    trigger:
      "h-7 gap-1.5 rounded-sm border-input px-2 text-[13px] leading-4.5 shadow-(--shadow-button-secondary)",
    menu: "rounded-md",
    item: "min-h-6.5 gap-2 rounded-[5px] text-[13px] leading-4.5",
    indicator: "h-4.5 w-3",
    check: { size: 12, strokeWidth: 2.6 },
  },
  md: {
    trigger: "",
    menu: "rounded-[9px]",
    // 38 — which the board gives as a number and then as the reason for it: "two below
    // `--control-height`, so the menu never looks taller than the control it came out of". The
    // reason is what is written down here, because it is the half of the pair that keeps being
    // true. A literal 38 is 38 whatever the trigger becomes, and a trigger that came down to 36
    // would open 38px rows — which is the defect the Sizes note was raised about, arriving from
    // the other side. `--control-height` is 2.5rem as the board draws it, so this is 38.
    item: "min-h-[calc(var(--control-height)-2px)] gap-2.5 rounded-sm has-[[data-slot=select-item-description]]:pt-1.75 has-[[data-slot=select-item-description]]:pb-2",
    indicator: "h-5 w-3.5",
    check: { size: 14, strokeWidth: 2.4 },
  },
};

/**
 * The size is carried on the Root rather than asked for part by part, because the trigger and
 * the menu are not in one tree — the popup is portalled — and the failure this exists to make
 * unrepresentable is exactly a half-applied size: a 28px trigger that opens 40px rows, which is
 * what nine hand-written overrides on the trigger alone used to produce.
 */
const SelectSizeContext = createContext<SelectSize>("md");

/**
 * Base UI's Root plus the size. Everything else about it — the generic value, the form
 * contract, the open state — is passed straight through and is still Base UI's.
 */
function SelectRoot<Value, Multiple extends boolean | undefined = false>({
  size = "md",
  ...props
}: SelectPrimitive.Root.Props<Value, Multiple> & { size?: SelectSize }) {
  return (
    <SelectSizeContext value={size}>
      <SelectPrimitive.Root {...props} />
    </SelectSizeContext>
  );
}

function SelectLabel({ className, ...props }: SelectPrimitive.Label.Props) {
  return (
    <SelectPrimitive.Label
      {...props}
      data-slot="select-label"
      className={merge(labelStyles, className)}
    />
  );
}

function SelectTrigger({ className, children, ...props }: SelectPrimitive.Trigger.Props) {
  const size = use(SelectSizeContext);
  return (
    <SelectPrimitive.Trigger
      {...props}
      data-slot="select-trigger"
      data-size={size}
      className={merge(
        cn(
          controlStyles,
          "group flex items-center gap-2.5 text-left hover:not-disabled:bg-band data-popup-open:border-(--focus-border) data-popup-open:ring-3 data-popup-open:ring-(--focus-halo)",
          SIZES[size].trigger,
        ),
        className,
      )}
    >
      {children}
      {/* A double chevron in every size: the control offers a value above and a value below, and
          a single caret says "expand" where this says "change". 12 square at `sm` and `md`. The
          caret joins the border in taking the focus edge while the menu is open, so the whole control
          moves as one thing rather than the edge lighting up around a grey mark. */}
      <SelectPrimitive.Icon
        data-slot="select-icon"
        className="ml-auto flex shrink-0 text-muted-foreground group-data-popup-open:text-foreground"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M8 9l4-4 4 4" />
          <path d="M16 15l-4 4-4-4" />
        </svg>
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value
      {...props}
      data-slot="select-value"
      className={merge("min-w-0 flex-1 truncate", className)}
    />
  );
}

function SelectContent({ className, children, ...props }: SelectPrimitive.Popup.Props) {
  const size = use(SelectSizeContext);
  return (
    <SelectPrimitive.Portal>
      {/* Above a dialog (z-50): a select opened inside one must not open behind it. */}
      <SelectPrimitive.Positioner className="z-60" sideOffset={6} alignItemWithTrigger={false}>
        <SelectPrimitive.Popup
          {...props}
          data-slot="select-content"
          data-size={size}
          className={merge(
            cn(
              "max-h-(--available-height) min-w-(--anchor-width) max-w-[calc(100vw-2rem)] origin-(--transform-origin) overflow-y-auto border border-border bg-card p-1 text-foreground shadow-(--shadow-popup)",
              SIZES[size].menu,
            ),
            className,
          )}
        >
          <SelectPrimitive.List>{children}</SelectPrimitive.List>
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

function SelectItem({
  className,
  children,
  ...props
}: ComponentProps<typeof SelectPrimitive.Item>) {
  const size = use(SelectSizeContext);
  const check = SIZES[size].check;
  return (
    <SelectPrimitive.Item
      {...props}
      data-slot="select-item"
      data-size={size}
      className={merge(
        cn(
          // `--selected` belongs to the highlight alone. A ground moves under the pointer, so it
          // reads as where the reader is; a check stays put, so it reads as what is already true.
          // Spending one channel on both meanings is what produced two adjacent rows in the same
          // tint, fused into a single shape with the boundary between the two states lost inside
          // it. The selection keeps the check and the 500 weight, which is how a menu has marked
          // it since long before this one.
          //
          // An unavailable option stays legible at `--subtle-foreground` rather than fading the
          // whole row to half: its trailing slot carries the reason it cannot be picked, and a
          // reason nobody can read is not one.
          //
          // A reason takes the check's lane rather than sitting beside it: an option that cannot be
          // chosen can never be checked, and an empty lane after the reason would pull it off the
          // right edge the ticks above it end on.
          "flex cursor-pointer items-center px-2 py-1 outline-none select-none data-highlighted:bg-selected data-selected:font-medium data-disabled:pointer-events-none data-disabled:text-subtle-foreground has-[[data-slot=select-item-description]]:items-start",
          "has-[[data-slot=select-item-reason]]:[&>[data-slot=select-item-lane]]:hidden",
          "text-(length:--text-ui) leading-(--text-ui--line-height)",
          SIZES[size].item,
        ),
        className,
      )}
    >
      {children}
      {/* A fixed lane on every row, filled or not, so the ticks line up down the menu. Its height
          is the first line's, so the check sits on the label rather than on the middle of a row
          that has grown a second line under it. */}
      <span
        data-slot="select-item-lane"
        className={cn("ml-auto flex shrink-0 items-center justify-center", SIZES[size].indicator)}
        aria-hidden="true"
      >
        {/* Kept mounted so ticking and clearing can animate (motion.css), while a menu that
            opens on a choice already made shows its check already drawn. Drawn from the short
            arm, as a hand would. */}
        <SelectPrimitive.ItemIndicator keepMounted data-slot="select-item-indicator">
          <svg
            width={check.size}
            height={check.size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={check.strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 12l5 5L20 6" pathLength={1} />
          </svg>
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  );
}

function SelectItemText({ className, ...props }: SelectPrimitive.ItemText.Props) {
  return (
    <SelectPrimitive.ItemText
      {...props}
      data-slot="select-item-text"
      className={merge("flex min-w-0 flex-1 flex-col gap-0.5", className)}
    />
  );
}

function SelectItemDescription({ className, ...props }: ComponentProps<"span">) {
  return (
    <span
      {...props}
      data-slot="select-item-description"
      className={cn(
        "font-normal text-(length:--text-help) leading-(--text-help--line-height) text-muted-foreground",
        className,
      )}
    />
  );
}

/**
 * Why a disabled option cannot be picked — "Soon", "Not yet" — in the check's lane. It stays in
 * the list rather than disappearing, because an option that exists but is not offered yet is
 * more honest visible than absent. Mono at 10, the same register as a label's code.
 */
function SelectItemReason({ className, ...props }: ComponentProps<"span">) {
  return (
    <span
      {...props}
      data-slot="select-item-reason"
      className={cn(
        "ml-auto shrink-0 font-mono text-[10px] leading-3 font-normal tracking-[0.08em] uppercase text-subtle-foreground",
        className,
      )}
    />
  );
}

/** Supported Field parts only; Root retains Base UI's generic value and form contract. */
export const Select = {
  Root: SelectRoot,
  Label: SelectLabel,
  Trigger: SelectTrigger,
  Value: SelectValue,
  Content: SelectContent,
  Item: SelectItem,
  ItemText: SelectItemText,
  ItemDescription: SelectItemDescription,
  ItemReason: SelectItemReason,
};
